/* eslint-disable react/react-in-jsx-scope */
import { CustomButton, CustomDrawer } from '@components';
import { Box, Chip, IconButton, TextField, Typography } from '@mui/material';
import { COLORS } from '@styles';
import { appContext } from '@utils';
import { useFormik } from 'formik';
import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import * as Yup from 'yup';
import PercentIcon from '@mui/icons-material/Percent';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { toast } from 'react-toastify';
import NoResultsImage from '@assets/img/no-results-logs.png';
import { BillingModel, ClientOrganization } from '@graphql';
import { RequestStatus } from '@store';

const makeStyles = () => ({
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '85vh',
  } as React.CSSProperties,
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    padding: '0 1rem',
  } as React.CSSProperties,
  formSection: {
    flexShrink: 0,
  } as React.CSSProperties,
  organizationHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem',
    paddingTop: '0.5rem',
  },
  organizationName: {
    fontSize: '1rem',
    fontWeight: 600,
    color: COLORS.NEUTRAL_WHITE,
  },
  tierChip: {
    backgroundColor: COLORS.ELEKTRAFI_NEUTRAL,
    color: COLORS.NEUTRAL_WHITE,
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  formItems: {
    margin: '1rem 0 0 0',
    borderRadius: '1rem',
  },
  inputBorder: {
    borderRadius: '1rem',
  },
  description: {
    color: COLORS.NEUTRAL_LIGHT_0,
    fontSize: '0.75rem',
    padding: '0 1rem',
    marginTop: '0.5rem',
  },
  availableSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    marginTop: '1.5rem',
  } as React.CSSProperties,
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexShrink: 0,
  },
  discountCardWrapper: {
    border: `1px solid ${COLORS.NEUTRAL_DARK_1}`,
    borderRadius: '0.5rem',
    padding: '1rem',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '100%',
  } as React.CSSProperties,
  discountCodeCard: {
    marginBottom: '0.75rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '0.5rem',
  } as React.CSSProperties,
  discountCodeInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    paddingBlock: '0.37rem',
  } as React.CSSProperties,
  discountCodeTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: COLORS.NEUTRAL_WHITE,
  },
  discountCodeValue: {
    fontSize: '0.75rem',
    fontWeight: 400,
    color: COLORS.NEUTRAL_LIGHT_0,
  },
  discountCodeActions: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    textAlign: 'center',
  } as React.CSSProperties,
  emptyStateImage: {
    marginBottom: '1rem',
  },
  emptyStateText: {
    color: COLORS.NEUTRAL_LIGHT_0,
    fontSize: '0.875rem',
  },
  button: {
    padding: '1rem 0 0 0',
  },
  chip: {
    backgroundColor: COLORS.ELEKTRAFI_NEUTRAL,
    color: COLORS.NEUTRAL_WHITE,
    fontWeight: 600,
    fontSize: '0.75rem',
  },
});

interface IApplyDiscountCodeDrawerProps {
  open: boolean;
  onClose: () => void;
  organization: ClientOrganization | null;
}

export const ApplyDiscountCodeDrawer = observer(
  ({ open, onClose, organization }: IApplyDiscountCodeDrawerProps) => {
    const sx = makeStyles();

    const {
      billingStore: {
        organizationBilling,
        applyDiscountCodeToOrg,
        removeDiscountCodeFromOrg,
        applyDiscountCodeData,
        removeDiscountCodeData,
      },
    } = useContext(appContext);

    const validationSchema = Yup.object().shape({
      discountCode: Yup.string().required('Discount code is required.'),
    });

    const formik = useFormik({
      initialValues: {
        discountCode: '',
      },
      validationSchema,
      onSubmit: async (values, { resetForm }) => {
        if (!organization?.id) return;
        
        // Prevent applying discount codes for Invoice billing organizations
        if (organizationBilling?.billingModel === BillingModel.INVOICE) {
          toast('Discount codes are not supported for Invoice payment method', { type: 'error' });
          return;
        }

        const result = await applyDiscountCodeToOrg({
          promotionCode: values.discountCode,
          orgId: organization.id,
        });

        if (result) {
          resetForm();
        }
      },
      validateOnChange: false,
    });

    const handleCopyCode = (code: string) => {
      navigator.clipboard.writeText(code);
      toast('Discount code copied to clipboard!', { type: 'success' });
    };

    const handleDeleteCode = async () => {
      if (!organization?.id) return;

      await removeDiscountCodeFromOrg({
        orgId: organization.id,
      });
    };

    const appliedDiscount = organizationBilling?.discount;
    const hasAppliedDiscount = !!appliedDiscount;
    const discountCount = hasAppliedDiscount ? 1 : 0;
    const isLoading = applyDiscountCodeData.status === RequestStatus.LOADING ||
                      removeDiscountCodeData.status === RequestStatus.LOADING;
    const isInvoiceBilling = organizationBilling?.billingModel === BillingModel.INVOICE;

    return (
      <CustomDrawer
        open={open}
        onClose={onClose}
        headerLabel="Apply Discount Code"
        headerIcon={<PercentIcon />}
      >
        <form style={sx.formWrapper} onSubmit={formik.handleSubmit}>
          <div style={sx.formContainer}>
            <div style={sx.formSection}>
              <div style={sx.organizationHeader}>
                <Typography style={sx.organizationName}>{organization?.name}</Typography>
                <Chip
                  label={organization?.currentTier?.name || 'Core'}
                  style={sx.tierChip}
                  size="small"
                />
              </div>

              <Typography variant="subtitle2" style={{ marginTop: '0.5rem' }}>
                Discount Code
              </Typography>
              <TextField
                style={sx.formItems}
                fullWidth
                placeholder="Discount Code"
                name="discountCode"
                onChange={formik.handleChange}
                value={formik.values.discountCode}
                variant="outlined"
                disabled={hasAppliedDiscount || isInvoiceBilling}
                InputProps={{
                  style: sx.inputBorder,
                  startAdornment: (
                    <PercentIcon style={{ marginRight: '0.5rem', color: COLORS.NEUTRAL_LIGHT_0 }} />
                  ),
                }}
                error={!!formik.errors.discountCode}
                helperText={formik.errors.discountCode}
              />
              <Typography style={sx.description}>
                {isInvoiceBilling
                  ? 'Discount codes are not available for organizations using the Invoice payment method.'
                  : 'This discount applies to the organization\'s plan and add-ons at checkout. Each organization can have one active discount code at a time.'}
              </Typography>

              <div style={sx.button}>
                <CustomButton
                  variant="default"
                  type="submit"
                  isLoading={isLoading}
                  disabled={hasAppliedDiscount || isInvoiceBilling}
                >
                  Apply Discount Code
                </CustomButton>
              </div>
            </div>

            <div style={sx.availableSection}>
              <div style={sx.sectionHeader}>
                <Typography variant="subtitle1">Discount Code</Typography>
                <Chip
                  label={`${discountCount} of 1 Code`}
                  style={sx.chip}
                  size="small"
                />
              </div>

              <Box style={sx.discountCardWrapper}>
                {!hasAppliedDiscount && (
                  <div style={sx.emptyState}>
                    <img src={NoResultsImage} alt="No discount code" style={sx.emptyStateImage} />
                    <Typography style={sx.emptyStateText}>
                      No discount code applied yet.
                    </Typography>
                  </div>
                )}
                {hasAppliedDiscount && appliedDiscount && (
                  <div style={sx.discountCodeCard}>
                    <div style={sx.discountCodeInfo}>
                      <Typography style={sx.discountCodeTitle}>
                        %{appliedDiscount.percentOff} Off in Plan and Add-Ons
                      </Typography>
                      <Typography style={sx.discountCodeValue}>{appliedDiscount.code}</Typography>
                    </div>
                    <div style={sx.discountCodeActions}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(appliedDiscount.code)}
                        sx={{ color: COLORS.NEUTRAL_WHITE }}
                        disabled={isLoading}
                      >
                        <ContentCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleDeleteCode}
                        sx={{ color: COLORS.NEGATIVE_NEUTRAL }}
                        disabled={isLoading}
                      >
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                )}
              </Box>
            </div>
          </div>
        </form>
      </CustomDrawer>
    );
  },
);

